import { Field, TextField, Button } from '@rtkwlf/fenrir-react';
import get from 'lodash/get';
import type { FieldErrors, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid, type GridProps } from '@packages/fenrir/components/grid';
import { Stack } from '@packages/fenrir/components/stack';

import { BulkImportIPRange } from '../bulk-import-ip-range/bulk-import-ip-range';
import { ipRangeConfig, portsConfig } from '../ip-controls-config';

const itemSizes: GridProps['size'] = {
  sm: 12,
  md: 6,
  lg: 3,
};

interface AddIPControlsProps {
  ipControlsItemKey: string;
  ipControlsListKey: 'scanExclusions';
  onAdd: () => void;
  onFileUpload?: () => void;
  errors: FieldErrors;
  portsParams: {
    visible: boolean;
    disabled: boolean;
    disabledHelperText: string;
  };
  formMethods: UseFormReturn;
}

export const AddIPControls: React.FC<AddIPControlsProps> = ({
  ipControlsItemKey,
  ipControlsListKey,
  onAdd,
  onFileUpload,
  portsParams = {
    visible: false,
    disabled: false,
    disabledHelperText: '',
  },
  errors,
  formMethods,
}) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { clearErrors, getValues, getFieldState, register, resetField } =
    formMethods;

  const { onChange: onChangeIpRange, ...restIpRange } = register(
    `${ipControlsItemKey}.ipRange`,
    ipRangeConfig,
  );
  const { onChange: onChangePorts, ...restPorts } = register(
    `${ipControlsItemKey}.ports`,
    portsConfig,
  );

  return (
    <Grid container spacing={{ xs: 2, md: 1 }} width="100%">
      <Grid size={itemSizes}>
        {/* ip Address/Range Field */}
        <Field.Root
          isRequired
          appearance={
            get(errors, `${ipControlsItemKey}.ipRange`) ? 'danger' : 'neutral'
          }
        >
          <Field.Label display="flex">
            {t('addIPControls.ipAddressRange')}
          </Field.Label>

          <Field.Content>
            <TextField
              placeholder={t('addIPControls.ipAddressRangePlaceholder')}
              {...restIpRange}
              onChange={(e) => {
                onChangeIpRange(e);
                if (e.target.value.length === 0)
                  resetField(`${ipControlsItemKey}.ipRange`, {
                    keepDirty: false,
                  });
                if (getFieldState(`${ipControlsItemKey}.ipRange`).error)
                  clearErrors(`${ipControlsItemKey}.ipRange`);
              }}
            />
          </Field.Content>

          {get(errors, `${ipControlsItemKey}.ipRange`) ? (
            <Field.Message>
              {get(errors, `${ipControlsItemKey}.ipRange`)?.message as string}
            </Field.Message>
          ) : (
            <Stack direction="column" gap={1}>
              <Field.Info>
                {t('addIPControls.ipAddressRangeDescriptionLine1')}
              </Field.Info>
              <Field.Info>
                {t('addIPControls.ipAddressRangeDescriptionLine2')}
              </Field.Info>
            </Stack>
          )}
        </Field.Root>
      </Grid>

      {portsParams?.visible && (
        <Grid size={itemSizes}>
          {/* Ports Field */}
          <Field.Root
            appearance={
              get(errors, `${ipControlsItemKey}.ports`) ? 'danger' : 'neutral'
            }
          >
            <Field.Label display="flex">
              {`${t('addIPControls.ports')} ${t('ui:optional')}`}
            </Field.Label>
            <Field.Content>
              <TextField
                placeholder={t('addIPControls.portsPlaceholder')}
                {...restPorts}
                onChange={(e) => {
                  onChangePorts(e);
                  if (e.target.value.length === 0)
                    resetField(`${ipControlsItemKey}.ports`, {
                      keepDirty: false,
                    });
                  if (getFieldState(`${ipControlsItemKey}.ports`).error)
                    clearErrors(`${ipControlsItemKey}.ports`);
                }}
                isDisabled={portsParams?.disabled}
              />
            </Field.Content>
            {get(errors, `${ipControlsItemKey}.ports`) ? (
              <Field.Message>
                {get(errors, `${ipControlsItemKey}.ports`)?.message as string}
              </Field.Message>
            ) : (
              <Stack direction="column" gap={1}>
                {portsParams?.disabled ? (
                  <Field.Info>{portsParams?.disabledHelperText}</Field.Info>
                ) : (
                  <>
                    <Field.Info>
                      {t('addIPControls.portsDescriptionLine1')}
                    </Field.Info>
                    <Field.Info>
                      {t('addIPControls.portsDescriptionLine2')}
                    </Field.Info>
                  </>
                )}
              </Stack>
            )}
          </Field.Root>
        </Grid>
      )}

      <Grid size={itemSizes}>
        <Stack direction="row" pt={{ xs: 1, lg: 4 }} gap={1}>
          <Button
            onClick={onAdd}
            isDisabled={!getFieldState(ipControlsItemKey).isDirty}
          >
            {t('ui:add')}
          </Button>
          <BulkImportIPRange
            ipControlsItemKey={ipControlsItemKey}
            ipControlsListKey={ipControlsListKey}
            ipControlsList={getValues(ipControlsListKey)}
            onFileUpload={onFileUpload}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
